import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';

// Lotin ↔ Kiril harflarini normalizatsiya qilish
// Qidiruv qaysi alifboda kiritilsa ham ishlaydi
const LATIN_TO_CYRILLIC = {
    'a': 'а', 'b': 'б', 'v': 'в', 'g': 'г', 'd': 'д', 'e': 'е', 'yo': 'ё',
    'j': 'ж', 'z': 'з', 'i': 'и', 'y': 'й', 'k': 'к', 'l': 'л', 'm': 'м',
    'n': 'н', 'o': 'о', 'p': 'п', 'r': 'р', 's': 'с', 't': 'т', 'u': 'у',
    'f': 'ф', 'x': 'х', 'ts': 'ц', 'ch': 'ч', 'sh': 'ш', 'q': 'қ',
    'ng': 'нг', 'h': 'ҳ', "'": 'ъ', 'gh': 'ғ',
};

const CYRILLIC_TO_LATIN = {
    'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'yo',
    'ж': 'j', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
    'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
    'ф': 'f', 'х': 'x', 'ц': 'ts', 'ч': 'ch', 'ш': 'sh', 'қ': 'q',
    'ҳ': 'h', 'ғ': 'gh', 'ъ': "'", 'э': 'e', 'ю': 'yu', 'я': 'ya',
    'ы': 'i', 'ь': '',
};

function cyrillicToLatin(str) {
    return str.split('').map(ch => CYRILLIC_TO_LATIN[ch] ?? ch).join('');
}

function latinToCyrillic(str) {
    // Murakkab digraph (2 harf) ni avval almashtirish
    let result = str;
    const digraphs = ['yo', 'ts', 'ch', 'sh', 'ng', 'gh', 'yu', 'ya'];
    for (const d of digraphs) {
        result = result.split(d).join(LATIN_TO_CYRILLIC[d] || d);
    }
    return result.split('').map(ch => LATIN_TO_CYRILLIC[ch] ?? ch).join('');
}

// Matnni hamma variantga normallashtirish (lowercase + lotin + kiril)
function normalize(str) {
    const s = str.toLowerCase();
    return [s, cyrillicToLatin(s), latinToCyrillic(s)];
}

function matchesSearch(label, query) {
    if (!query) return true;
    const q = query.toLowerCase();
    const variants = normalize(label);
    return variants.some(v => v.includes(q));
}

export default function CustomSelect({ 
    options, 
    value, 
    onChange, 
    placeholder = "Tanlang...", 
    className = "", 
    disabled = false,
    required = false,
    error = false,
    searchable = false,
    searchPlaceholder = "Qidirish...",
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef(null);
    const searchRef = useRef(null);

    // Close on click outside
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
            // Focus search input when opened
            if (searchable) {
                setTimeout(() => searchRef.current?.focus(), 50);
            }
        }
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isOpen, searchable]);

    const selectedOption = options.find(o => String(o.value) === String(value));

    const filteredOptions = searchable && searchQuery
        ? options.filter(o => matchesSearch(o.label, searchQuery))
        : options;

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl transition-all shadow-sm ring-1 
                    ${error ? 'bg-error/5 ring-error/50 text-error' : 'bg-surface-2 ring-border hover:ring-brand/50'} 
                    ${isOpen ? 'ring-brand' : ''} 
                    ${disabled ? 'opacity-60 cursor-not-allowed hidden-pointer-events' : ''}
                    text-left text-sm font-medium
                `}
            >
                <span className={`block truncate ${!selectedOption ? 'text-muted' : 'text-primary'}`}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-brand' : 'text-muted'}`} />
            </button>

            {/* Hidden native input for required validation */}
            {required && (
                <input
                    type="text"
                    required
                    value={value || ''}
                    className="absolute opacity-0 pointer-events-none w-0 h-0"
                    onChange={() => {}}
                    tabIndex={-1}
                />
            )}

            {isOpen && (
                <div className="absolute z-[100] mt-1.5 w-full bg-surface border border-border rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] py-1.5 animate-in fade-in zoom-in-95 duration-200">
                    {/* Search input */}
                    {searchable && (
                        <div className="px-2 pb-1.5 border-b border-border mb-1">
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-2 ring-1 ring-border focus-within:ring-brand transition-all">
                                <Search className="w-3.5 h-3.5 text-muted flex-shrink-0" />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder={searchPlaceholder}
                                    className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted min-w-0"
                                    onClick={e => e.stopPropagation()}
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={e => { e.stopPropagation(); setSearchQuery(''); }}
                                        className="text-muted hover:text-primary transition-colors"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    <ul className="max-h-56 overflow-y-auto no-scrollbar outline-none p-1 flex flex-col gap-0.5">
                        {filteredOptions.map((opt) => {
                            const isSelected = String(opt.value) === String(value);
                            return (
                                <li key={opt.value}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onChange(opt.value);
                                            setIsOpen(false);
                                            setSearchQuery('');
                                        }}
                                        className={`
                                            w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-left
                                            ${isSelected 
                                                ? 'bg-brand/10 text-brand font-bold' 
                                                : 'text-primary hover:bg-surface-2 hover:text-brand font-medium'
                                            }
                                        `}
                                    >
                                        <span className="block truncate">{opt.label}</span>
                                        {isSelected && <Check className="w-4 h-4 flex-shrink-0" />}
                                    </button>
                                </li>
                            );
                        })}
                        {filteredOptions.length === 0 && (
                            <li className="px-4 py-3 text-sm text-muted text-center">
                                {searchQuery ? `"${searchQuery}" — topilmadi` : 'Hech narsa topilmadi'}
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
